import { makeAutoObservable, runInAction } from 'mobx';
import agent, { resolvePreviewHostUrl } from '../services/api/agent';
import { store } from './store';
import SnackbarUtils from '../components/snackbarUtils/SnackbarUtilsConfigurator';

export interface IPreviewShareLink {
  publicId: string;
  previewName?: string;
  courseId: number;
  coursePageId?: number;
  createdByOwnerId: number;
  language: string;
  accessMode: number;
  commentVisibilityMode?: number;
  allowComments?: boolean;
  privateComments?: boolean;
  requiresPassword: boolean;
  expiresAtUtc: string | null;
  maxViews?: number;
  viewCount: number;
  revokedAtUtc?: string;
  createdAtUtc: string;
  lastAccessAtUtc?: string;
  isExpired: boolean;
  isRevoked: boolean;
  invitedEmails: string[];
  shareUrl: string;
}

type TPreviewShareLinkApi = Omit<IPreviewShareLink, 'shareUrl'>;

export default class PreviewStore {
  previewSource = '';
  currentPreviewCourseID = 0;
  currentPreviewCourseTitle = '';
  currentPreviewPageID = 0;
  coursePreviewOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  openPagePreview = async () => {
    if (store.pageStore.coursePage === undefined) return;

    if (store.pageStore.coursePage.courseID !== this.currentPreviewCourseID) {
      this.openPreview(store.pageStore.coursePage.courseID, store.pageStore.coursePage.coursePageID);
      return;
    } else if (store.pageStore.coursePage.coursePageID !== this.currentPreviewPageID) {
      this.openPreview(store.pageStore.coursePage.courseID, store.pageStore.coursePage.coursePageID);
      return;
    }
    this.setPreviewOpen(false);
  };

  openCoursePreview = async () => {
    if (store.courseStore.courseViewModel === undefined) return;
    if (store.courseStore.courseViewModel.courseID !== this.currentPreviewCourseID) {
      this.openPreview(store.courseStore.courseViewModel.courseID);
      return;
    }

    this.setPreviewOpen(false);
  };

  openCoursePreviewByID = async (courseID: number) => {
    if (courseID !== this.currentPreviewCourseID) {
      this.openPreview(courseID);
      return;
    }

    this.setPreviewOpen(false);
  };

  private resolvePreviewPageId = async (courseID: number, pageID?: number): Promise<number | undefined> => {
    let page = 1;
    if (pageID === undefined) {
      const coursePages = await agent.Course.getPages(courseID);
      this.setCurrentPreviewCourseTitle(coursePages.title);
      const pages = Object.keys(coursePages.pages);
      if (pages.length > 0) {
        page = parseInt(coursePages.pages[0].id);
      } else {
        SnackbarUtils.info('Course has no pages');
        return undefined;
      }
    } else {
      this.setCurrentPreviewCourseTitle(store.pageStore.coursePage ? store.pageStore.coursePage.title : '');
    }

    return pageID ? pageID : page;
  };

  getLegacyPreview = async (courseID: number, pageID?: number) => {
    const previewPageId = await this.resolvePreviewPageId(courseID, pageID);
    if (previewPageId === undefined) {
      return undefined;
    }

    const legacyPreviewUrl = await agent.ExportStaticHtml.PreviewPage(courseID, previewPageId);
    return resolvePreviewHostUrl(legacyPreviewUrl);
  };

  getPreview = async (courseID: number, pageID?: number) => {
    const previewPageId = await this.resolvePreviewPageId(courseID, pageID);
    if (previewPageId === undefined) {
      return undefined;
    }

    try {
      const launchSession = await agent.Preview.CreateInternalSession(courseID, previewPageId);
      return resolvePreviewHostUrl(launchSession.launchUrl);
    } catch {
      return await this.getLegacyPreview(courseID, previewPageId);
    }
  };

  openPreview = async (courseID: number, pageID?: number) => {
    this.previewSource = ''; //Resets previewSource
    this.setPreviewOpen(true);
    const source = await this.getPreview(courseID, pageID);
    if (source === undefined) {
      this.setPreviewOpen(false);
      SnackbarUtils.error('Unable to open preview');
      return;
    }
    runInAction(() => {
      this.previewSource = source;
    });
    this.setCurrentPreviewCourseID(courseID);
    this.setCurrentPreviewPageID(pageID ? pageID : 1);
  };

  createShareLink = async (
    courseID: number,
    pageID: number | undefined,
    expiresAtUtc: string | null,
    password?: string,
    maxViews?: number,
    language = 'en',
    previewName?: string,
    accessMode = 1,
    allowComments = true,
    privateComments = false,
    invitedEmails?: string[],
    publicId?: string,
  ): Promise<IPreviewShareLink> => {
    const response = await agent.Preview.CreateShareLink(
      courseID,
      pageID,
      language,
      expiresAtUtc,
      password,
      maxViews,
      previewName,
      accessMode,
      allowComments,
      privateComments,
      invitedEmails,
      publicId,
    );
    return this.withShareUrl(response);
  };

  revokeShareLink = async (publicId: string) => {
    await agent.Preview.RevokeShareLink(publicId);
  };

  deleteShareLink = async (publicId: string) => {
    await agent.Preview.DeleteShareLink(publicId);
  };

  updateShareLink = async (
    publicId: string,
    coursePageID: number | undefined,
    language: string,
    expiresAtUtc: string | null,
    password?: string,
    removePassword = false,
    maxViews?: number,
    previewName?: string,
    accessMode = 1,
    allowComments = true,
    privateComments = false,
    invitedEmails?: string[],
  ): Promise<IPreviewShareLink> => {
    const response = await agent.Preview.UpdateShareLink(
      publicId,
      coursePageID,
      language,
      expiresAtUtc,
      password,
      removePassword,
      maxViews,
      previewName,
      accessMode,
      allowComments,
      privateComments,
      invitedEmails,
    );
    return this.withShareUrl(response);
  };

  listShareLinks = async (courseID: number, includeRevoked = true): Promise<IPreviewShareLink[]> => {
    const response = await agent.Preview.ListShareLinks(courseID, includeRevoked);
    return response.items.map(this.withShareUrl);
  };

  setCurrentPreviewCourseID = (n: number) => {
    this.currentPreviewCourseID = n;
  };

  buildShareUrl = (publicId: string) => {
    return `${window.location.origin}/preview/share/${encodeURIComponent(publicId)}`;
  };

  private withShareUrl = (link: TPreviewShareLinkApi): IPreviewShareLink => {
    const allowComments = link.allowComments ?? true;
    const privateComments = link.privateComments ?? link.commentVisibilityMode === 2;
    return {
      ...link,
      coursePageId: link.coursePageId ?? undefined,
      maxViews: link.maxViews ?? undefined,
      revokedAtUtc: link.revokedAtUtc ?? undefined,
      lastAccessAtUtc: link.lastAccessAtUtc ?? undefined,
      allowComments,
      privateComments,
      invitedEmails: link.invitedEmails ?? [],
      shareUrl: this.buildShareUrl(link.publicId),
    };
  };

  setCurrentPreviewCourseTitle = (s: string) => {
    this.currentPreviewCourseTitle = s;
  };

  setCurrentPreviewPageID = (n: number) => {
    this.currentPreviewPageID = n;
  };

  setPreviewOpen = (state: boolean) => {
    this.coursePreviewOpen = state;
    if (!state) {
      this.setCurrentPreviewCourseID(0);
      this.setCurrentPreviewPageID(0);
      this.previewSource = '';
    }
  };
}
